import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type HandoverSpec } from '@/designs-dev/handover/types';

// One live control panel mini per step / state, opened on the Documents tab
// and jumped straight to its state via the documents prop.
const screen = (documents?: { upload?: boolean }) => (
  <ControlPanel embedded initialTab='documents' documentsInitial={documents} />
);

// The CAM-side handover for Key Documents V0: CAMs upload and manage the care
// recipient's documents on the control panel, and the care circle reads them
// in the member app. The carer-facing mobile UI lives in the Care hub.
export const documentsSpec: HandoverSpec = {
  id: 'documents',
  title: 'Key documents',
  screen: 'desktop',
  description:
    'A single home for the care recipient’s documents. CAMs upload, tag, search, edit and delete documents from the control panel; the care circle sees them in the member app and can upload their own. Documents can be linked to the actions they support.',
  links: [
    {
      label: 'Project: Key Documents V0 (Linear)',
      url: 'https://linear.app/karehero/project/key-documents-v0-initial-setup-29e97808f0d9',
    },
    { label: 'Live prototype (CAM control panel)', url: '/care-search' },
    { label: 'Member app (Profile tab)', url: '/karehero-app/profile' },
    { label: 'Call notes handover', url: '/handover/calls' },
  ],
  flows: [
    {
      title: 'Upload a document',
      steps: [
        {
          id: 'list',
          title: 'Documents list',
          tap: 'Control panel › Documents tab',
          render: () => screen(),
        },
        {
          id: 'upload',
          title: 'Upload document',
          tap: 'Tap ‘Upload document’',
          render: () => screen({ upload: true }),
        },
        {
          id: 'uploaded',
          title: 'Uploaded',
          tap: 'Save — it appears at the top, the circle can see it',
          render: () => screen(),
        },
      ],
    },
  ],
  states: [
    {
      id: 'list',
      title: 'Documents list',
      kind: 'happy',
      repro: 'Documents tab. Seed documents with tags, uploader and date',
      render: () => screen(),
    },
    {
      id: 'upload-modal',
      title: 'Upload document',
      kind: 'happy',
      repro: 'Tap ‘Upload document’. Name, tags and an optional note',
      render: () => screen({ upload: true }),
    },
    {
      id: 'edit',
      title: 'Edit a document',
      kind: 'happy',
      repro: 'Row ⋯ menu › Edit. Same dialog, pre-filled, ‘Save’',
      render: () => screen(),
    },
    {
      id: 'search-filter',
      title: 'Search and tag filter',
      kind: 'happy',
      repro: 'Type in search or pick a tag — the list filters live',
      render: () => screen(),
    },
    {
      id: 'delete',
      title: 'Delete a document',
      kind: 'edge',
      repro: 'Row ⋯ menu › Delete. Confirm dialog before it goes',
      render: () => screen(),
    },
    {
      id: 'no-match',
      title: 'No documents match',
      kind: 'empty',
      repro: 'Search or tag filter with no matches — prompt to clear it',
      render: () => screen(),
    },
  ],
  requirements: [
    {
      text: 'Upload a document: file picker, then name, tags and an optional note',
      status: 'built',
      where: 'Documents tab › Upload document',
      note: 'File picker is a placeholder. PDF/DOC/DOCX/JPG/PNG, 10MB per the spec',
    },
    {
      text: 'Predefined tags (Find care, Power of attorney, Funding)',
      status: 'built',
      where: 'Upload / edit dialog and the tag filter',
    },
    {
      text: 'Documents list: name, tags, uploader, date, size',
      status: 'built',
      where: 'Documents tab',
    },
    {
      text: 'Search by name and filter by tag',
      status: 'built',
      where: 'Documents tab header',
    },
    {
      text: 'View / download a document',
      status: 'partial',
      where: 'Row ⋯ menu › View',
      note: 'Mocked — fires a toast. Needs signed-URL download from storage',
    },
    {
      text: 'Edit a document’s name, tags and note',
      status: 'built',
      where: 'Row ⋯ menu › Edit',
    },
    {
      text: 'Delete a document with a confirm',
      status: 'built',
      where: 'Row ⋯ menu › Delete',
    },
    {
      text: 'Carer mobile UI: list (icon, name, tags, filename, size, ‘New’ badge), upload flow, view/download/delete drawer',
      status: 'built',
      where: 'Member app › Care hub › Documents',
      note: 'Built in the member prototype (DocRow, DocSheets). Read + upload',
    },
    {
      text: 'Predefined document types (e.g. Funding Guide) seeded for every circle',
      status: 'not-built',
      note: 'In the project scope; no seeded type in the prototype yet',
    },
    {
      text: 'Link a document to an action',
      status: 'partial',
      note: 'Linking surface exists on calls/actions. Documents↔actions wiring is not complete',
    },
    {
      text: 'In-app notification to the care circle when a CAM uploads a document',
      status: 'partial',
      note: 'Upload toast says the circle can see it. No notification fires',
    },
    {
      text: 'Document storage & security infrastructure (GCS, signed URLs, malware scan)',
      status: 'out-of-scope',
      note: 'Backend — out of scope for this design handover',
    },
  ],
  components: {
    mui: [
      'Button',
      'Card',
      'Chip',
      'InputAdornment',
      'Menu',
      'MenuItem',
      'Snackbar',
      'TextField (search / select)',
    ],
    existing: ['Icon (llama)', 'Modal'],
    new: ['DocumentRow', 'DocumentDialog', 'DocRow', 'DocSheets'],
  },
  componentsNote:
    'DocumentRow + DocumentDialog are the CAM side (control panel); DocRow + DocSheets are the carer mobile side (member app). Both consume the shared Modal for the upload / edit / delete surfaces, so the dialog chrome stays consistent.',
};
